import dayjs from 'dayjs';
import { ONLINE_WITHIN_SECONDS, formatCompactDuration } from '@/utils/checkinStatus';

/**
 * Pure logic behind the Sync pill and its popover: what the backend's scheduler endpoints return, and how those rows
 * turn into the short state text the UI shows.
 *
 * Every endpoint here already exists in the tabasco backend
 * (SchedulerPublishController and LocationZonePlayerCommandHistoryController).
 * Nothing in this file talks to the network, so all of it is unit tested.
 */

/** One row of GET /api/scheduler/pending-changes. */
export interface PendingChange {
    id: number;
    created_at: string;
    /** playlist, schedule, tracks, zone_settings or zone_hours. */
    subject_type: string;
    subject_id: number | null;
    /** added, edited, removed or restored. */
    action: string;
    /** False for zone hours: shown, but it alone never needs a delivery. */
    requires_publish: boolean;
    user: { id: number; name: string } | null;
    playlist: { id: number; name: string | null } | null;
    /** The zone the change was logged on. For an inherited list this is the master. */
    zone: { id: number; name: string | null; account_name: string | null };
}

export interface PendingChangesPage {
    data: PendingChange[];
    /** Cursor for the next (older) page, or null when this was the last page. */
    next_before_id: number | null;
}

/** One row of GET /api/scheduler/publish-status. */
export interface PublishStatusRow {
    location_zone_id: number;
    /**
     * The requested zones this row answers for. A master answers with its own
     * row when it has players, and with a row for each subscriber. Missing
     * from backends older than this field.
     */
    requested_location_zone_ids?: number[];
    zone: { id: number; name: string | null; account_id: number | null; account_name: string | null };
    content_changed_at: string | null;
    last_published_at: string | null;
    players: number;
    players_behind: number;
    players_queued: number;
    players_issued: number;
    pull_status_unknown: boolean;
}

/**
 * The delivery status of one zone as the popover shows it.
 *
 * A change logged on a master zone is delivered to the master's own players,
 * if it has any, and to its subscribers' players. The status endpoint answers
 * a master with its own row and one row per subscriber, so a master's status
 * is the sum of those rows. `deliveryZoneIds` lists the zones whose players
 * receive the content: the zone itself, plus a master's subscribers.
 */
export interface ZoneDeliveryStatus {
    zoneId: number;
    deliveryZoneIds: number[];
    /**
     * The counts of each delivery zone on its own. A subscriber with a change
     * of its own is also a delivery zone of its master, so the pill adds these
     * up by zone to count each player once.
     */
    deliveryZones: DeliveryZoneCounts[];
    accountId: number | null;
    accountName: string | null;
    contentChangedAt: string | null;
    players: number;
    playersBehind: number;
    playersQueued: number;
    playersIssued: number;
    pullStatusUnknown: boolean;
}

/** One delivery zone's counts, from its status row. */
export interface DeliveryZoneCounts {
    zoneId: number;
    playersBehind: number;
    playersQueued: number;
    playersIssued: number;
}

/** One row of GET /api/location_zone_player/{id}/command_history. */
export interface CommandHistoryEntry {
    command_id: string;
    command: string;
    command_truncated: boolean;
    source: string;
    /** pending, skipped, acknowledged, cleared or removed_without_flush. */
    state: string;
    skipped_reason: string | null;
    issued_at: string | null;
    acknowledged_at: string | null;
    delivered_at: string | null;
    completed_at: string | null;
    /**
     * The user's name for a `user:<id>` source. Null for other sources and for a
     * deleted user. Missing until the backend sends it (SAU-621).
     */
    source_user_name?: string | null;
    /** True for a content command, which the player later downloads. Missing until SAU-621. */
    delivers_content?: boolean;
    /** True when the player reports this command as done. Missing until SAU-621. */
    reports_completion?: boolean;
}

/** One page of the command history, with the cursor for the next, older page. */
export interface CommandHistoryPage {
    data: CommandHistoryEntry[];
    next_cursor: string | null;
}

/** The hour the backend schedules a queued content delivery for, in the player's own time zone. */
export const NIGHTLY_DELIVERY_LABEL = '4 AM';

/**
 * The status endpoint and the publish endpoint take at most this many zone ids
 * per request (SchedulerPublishStatusRequest::MAX_ZONE_IDS).
 */
export const MAX_ZONE_IDS_PER_REQUEST = 100;

export function chunkIds<T>(ids: T[], size: number = MAX_ZONE_IDS_PER_REQUEST): T[][] {
    const chunks: T[][] = [];
    for (let index = 0; index < ids.length; index += size) {
        chunks.push(ids.slice(index, index + size));
    }
    return chunks;
}

// ---------------------------------------------------------------------------
// Zone status
// ---------------------------------------------------------------------------

/**
 * Sum status rows into one zone's status. One row is the ordinary case. Several
 * rows are a master's subscribers.
 */
export function aggregateStatusRows(zoneId: number, rows: PublishStatusRow[]): ZoneDeliveryStatus | null {
    if (rows.length === 0) {
        return null;
    }
    let latestChangedAt: string | null = null;
    for (const row of rows) {
        if (row.content_changed_at !== null && (latestChangedAt === null || row.content_changed_at > latestChangedAt)) {
            latestChangedAt = row.content_changed_at;
        }
    }
    return {
        zoneId,
        deliveryZoneIds: rows.map((row) => row.location_zone_id),
        deliveryZones: rows.map((row) => ({
            zoneId: row.location_zone_id,
            playersBehind: row.players_behind,
            playersQueued: row.players_queued,
            playersIssued: row.players_issued,
        })),
        accountId: rows[0].zone.account_id,
        accountName: rows[0].zone.account_name,
        contentChangedAt: latestChangedAt,
        players: rows.reduce((total, row) => total + row.players, 0),
        playersBehind: rows.reduce((total, row) => total + row.players_behind, 0),
        playersQueued: rows.reduce((total, row) => total + row.players_queued, 0),
        playersIssued: rows.reduce((total, row) => total + row.players_issued, 0),
        pullStatusUnknown: rows.some((row) => row.pull_status_unknown),
    };
}

/**
 * Status for each requested zone.
 *
 * The zones are asked in batches of {@link MAX_ZONE_IDS_PER_REQUEST}. Each row
 * names the requested zones it answers for, so a master's own row and its
 * subscribers' rows are summed into the master's status. A zone no row
 * answers for is one the caller cannot see or that has no players, and it
 * has no status.
 *
 * A backend older than `requested_location_zone_ids` sends rows without it.
 * Then a zone that answers with its own row is done, and a zone missing from
 * its batch is asked again on its own, so a master's subscriber rows can be
 * told apart. That fallback misses the subscribers of a master that has
 * players of its own.
 */
export async function loadZoneDeliveryStatuses(
    zoneIds: number[],
    fetchStatusRows: (zoneIds: number[]) => Promise<PublishStatusRow[]>
): Promise<Map<number, ZoneDeliveryStatus>> {
    const statusByZoneId = new Map<number, ZoneDeliveryStatus>();
    const uniqueZoneIds = [...new Set(zoneIds)];
    const batchResponses = await Promise.all(chunkIds(uniqueZoneIds).map((batch) => fetchStatusRows(batch)));
    const requested = new Set(uniqueZoneIds);
    const batchRows = batchResponses.flat();
    if (batchRows.every((row) => Array.isArray(row.requested_location_zone_ids))) {
        const rowsByRequestedZoneId = new Map<number, PublishStatusRow[]>();
        for (const row of batchRows) {
            for (const requestedZoneId of row.requested_location_zone_ids ?? []) {
                if (!requested.has(requestedZoneId)) continue;
                const rows = rowsByRequestedZoneId.get(requestedZoneId);
                if (rows) rows.push(row);
                else rowsByRequestedZoneId.set(requestedZoneId, [row]);
            }
        }
        for (const [requestedZoneId, rows] of rowsByRequestedZoneId) {
            statusByZoneId.set(requestedZoneId, aggregateStatusRows(requestedZoneId, rows)!);
        }
        return statusByZoneId;
    }
    for (const row of batchRows) {
        if (requested.has(row.location_zone_id) && !statusByZoneId.has(row.location_zone_id)) {
            statusByZoneId.set(row.location_zone_id, aggregateStatusRows(row.location_zone_id, [row])!);
        }
    }
    const unanswered = uniqueZoneIds.filter((zoneId) => !statusByZoneId.has(zoneId));
    // Ten at a time, so a page full of masters does not fire a hundred requests at once.
    for (const group of chunkIds(unanswered, 10)) {
        const answers = await Promise.all(group.map((zoneId) => fetchStatusRows([zoneId])));
        group.forEach((zoneId, index) => {
            const status = aggregateStatusRows(zoneId, answers[index]);
            if (status !== null) {
                statusByZoneId.set(zoneId, status);
            }
        });
    }
    return statusByZoneId;
}

export type ZoneSyncKind = 'waiting' | 'sent' | 'up_to_date' | 'not_sent' | 'no_players';

export interface ZoneSyncState {
    kind: ZoneSyncKind;
    label: string;
}

/**
 * The zone row's state, from the status counts.
 *
 * - A queued delivery wins: the content goes out at 4 AM player time, so the
 *   state is just "4 AM".
 * - Otherwise a delivery issued since the change means it was sent and some
 *   players have not pulled it yet.
 * - Otherwise no player behind means every player has the current content.
 * - Otherwise players are behind and nothing is queued or sent.
 */
export function deriveZoneSyncState(status: ZoneDeliveryStatus): ZoneSyncState {
    if (status.players === 0) {
        return { kind: 'no_players', label: 'No players' };
    }
    if (status.playersQueued > 0) {
        // The time alone says it is waiting: "4 AM", not "Waiting · 4 AM".
        return { kind: 'waiting', label: NIGHTLY_DELIVERY_LABEL };
    }
    if (status.playersIssued > 0) {
        return { kind: 'sent', label: `Sent · ${status.playersIssued} of ${status.players}` };
    }
    if (status.playersBehind === 0) {
        return { kind: 'up_to_date', label: 'Up to date' };
    }
    return { kind: 'not_sent', label: 'Not sent' };
}

// ---------------------------------------------------------------------------
// Change lines and grouping
// ---------------------------------------------------------------------------

/** "2:14 PM" today, "Oct 21" on an earlier day. */
export function formatSyncTime(value: string | null | undefined, nowMs: number): string {
    if (!value) {
        return '';
    }
    const moment = dayjs(value);
    if (!moment.isValid()) {
        return '';
    }
    return moment.isSame(dayjs(nowMs), 'day') ? moment.format('h:mm A') : moment.format('MMM D');
}

function playlistLabel(change: PendingChange): string | null {
    const name = change.playlist?.name;
    return typeof name === 'string' && name.trim() !== '' ? name.trim() : null;
}

/**
 * "Joel edited the Electronic1 schedule", built from the change log fields.
 *
 * The log records what kind of thing changed and how, not which field, so a
 * schedule edit reads "edited the Electronic1 schedule" rather than naming the
 * end time.
 */
export function describeChange(change: PendingChange): string {
    const who = change.user?.name?.trim() || 'System';
    const playlist = playlistLabel(change);
    switch (change.subject_type) {
        case 'playlist':
            return playlist ? `${who} ${change.action} the ${playlist} playlist` : `${who} ${change.action} a playlist`;
        case 'schedule':
            return playlist ? `${who} ${change.action} the ${playlist} schedule` : `${who} ${change.action} a schedule`;
        case 'tracks':
            return playlist ? `${who} changed the tracks in ${playlist}` : `${who} changed a playlist's tracks`;
        case 'zone_settings':
            return `${who} changed the zone settings`;
        case 'zone_hours':
            return `${who} changed the zone hours`;
        default:
            return `${who} ${change.action} ${change.subject_type.replace(/_/g, ' ')}`;
    }
}

/** One zone in the popover: the changes logged on it, newest first. */
export interface SyncZone {
    zoneId: number;
    zoneName: string | null;
    accountName: string | null;
    changes: PendingChange[];
    latestChangeAt: string;
}

/** Zones in order of their newest change, newest first. Keeps the feed's newest-first order inside each zone. */
export function groupChangesByZone(changes: PendingChange[]): SyncZone[] {
    const zonesById = new Map<number, SyncZone>();
    for (const change of changes) {
        const existing = zonesById.get(change.zone.id);
        if (existing) {
            existing.changes.push(change);
            if (change.created_at > existing.latestChangeAt) {
                existing.latestChangeAt = change.created_at;
            }
            continue;
        }
        zonesById.set(change.zone.id, {
            zoneId: change.zone.id,
            zoneName: change.zone.name,
            accountName: change.zone.account_name,
            changes: [change],
            latestChangeAt: change.created_at,
        });
    }
    return [...zonesById.values()].sort((left, right) => right.latestChangeAt.localeCompare(left.latestChangeAt));
}

/** A zone's location, from the location_zone search document. */
export interface ZoneLocation {
    id: number;
    name: string;
}

/**
 * A heading in the popover: one location and its zones. Until a zone's
 * location has loaded, the zone sits under its account name with no link.
 */
export interface SyncLocationGroup {
    key: string;
    location: ZoneLocation | null;
    /** Shown muted after the location name, when it says something the location name does not. */
    accountName: string | null;
    heading: string;
    zones: SyncZone[];
}

/**
 * Group zones under their location. Groups keep the order of their newest zone.
 * `locationForZone` answers from the zone lookup cache: a location, null when
 * the zone has none, or undefined while it is loading.
 */
export function groupZonesByLocation(
    zones: SyncZone[],
    locationForZone: (zoneId: number) => ZoneLocation | null | undefined
): SyncLocationGroup[] {
    const groupsByKey = new Map<string, SyncLocationGroup>();
    for (const zone of zones) {
        const location = locationForZone(zone.zoneId) ?? null;
        const accountName = zone.accountName?.trim() || null;
        const key = location ? `location:${location.id}` : `account:${accountName ?? ''}`;
        const existing = groupsByKey.get(key);
        if (existing) {
            existing.zones.push(zone);
            continue;
        }
        groupsByKey.set(key, {
            key,
            location,
            accountName: location && accountName && !location.name.includes(accountName) ? accountName : null,
            heading: location?.name ?? accountName ?? 'No location',
            zones: [zone],
        });
    }
    return [...groupsByKey.values()];
}

/** How many players a zone row lists by id instead of as a count. */
export const ROW_PLAYER_ID_LIMIT = 2;

/** The delivery zones whose players the rows list by id: zones with one or two players. */
export function zoneIdsNeedingRowPlayers(statuses: Iterable<ZoneDeliveryStatus>): { zoneIds: number[]; playerCount: number } {
    const zoneIds: number[] = [];
    let playerCount = 0;
    for (const status of statuses) {
        if (status.players >= 1 && status.players <= ROW_PLAYER_ID_LIMIT) {
            zoneIds.push(...status.deliveryZoneIds);
            playerCount += status.players;
        }
    }
    return { zoneIds: [...new Set(zoneIds)], playerCount };
}

/** Player ids by their zone, from Location Zone Player search records, in id order. */
export function playerIdsByZoneId(players: Record<string, unknown>[]): Map<number, number[]> {
    const idsByZoneId = new Map<number, number[]>();
    for (const player of players) {
        const playerId = Number(player.id);
        const zoneId = Number(player.location_zone_id);
        if (!Number.isInteger(playerId) || !Number.isInteger(zoneId)) continue;
        idsByZoneId.set(zoneId, [...(idsByZoneId.get(zoneId) ?? []), playerId]);
    }
    for (const ids of idsByZoneId.values()) ids.sort((left, right) => left - right);
    return idsByZoneId;
}

/**
 * The player ids a zone row shows in place of "1 player" / "2 players", or
 * null to show the count: three or more players, or ids not loaded yet.
 */
export function rowPlayerIds(status: ZoneDeliveryStatus | undefined, idsByZoneId: Map<number, number[]>): number[] | null {
    if (!status || status.players < 1 || status.players > ROW_PLAYER_ID_LIMIT) return null;
    const ids = status.deliveryZoneIds.flatMap((zoneId) => idsByZoneId.get(zoneId) ?? []);
    return ids.length > 0 ? ids : null;
}


const BEHIND_KINDS: ReadonlySet<ZoneSyncKind> = new Set(['waiting', 'sent', 'not_sent']);

/**
 * The zones whose players do not all have the change yet: their state is
 * "4 AM", "Sent" or "Not sent". A zone counts only when it has a change that
 * needs a delivery (zone hours alone never do) and its status has loaded.
 * "Up to date" and "No players" are not behind.
 */
function zonesBehindStatuses(
    changes: PendingChange[],
    statusByZoneId: Map<number, ZoneDeliveryStatus>
): ZoneDeliveryStatus[] {
    const changedZoneIds = new Set(changes.filter((change) => change.requires_publish).map((change) => change.zone.id));
    const behind: ZoneDeliveryStatus[] = [];
    for (const zoneId of changedZoneIds) {
        const status = statusByZoneId.get(zoneId);
        if (status && BEHIND_KINDS.has(deriveZoneSyncState(status).kind)) behind.push(status);
    }
    return behind;
}

export function countZonesBehind(changes: PendingChange[], statusByZoneId: Map<number, ZoneDeliveryStatus>): number {
    return zonesBehindStatuses(changes, statusByZoneId).length;
}

export interface PlayersBehindCounts {
    /** Players of the behind zones that do not have the current content. */
    playersBehind: number;
    /** Of those, players with no delivery queued or sent: the problem number. */
    playersNotSent: number;
}

/**
 * The pill's two numbers, summed over the delivery zones of the zones counted
 * as behind. Each delivery zone is counted once: a subscriber with a change of
 * its own is behind on its own and is also a delivery zone of its master.
 *
 * In the backend (LocationZoneContentStateQueries) players_issued counts only
 * players that are behind, but players_queued counts every player with a
 * waiting delivery, behind or not. So "behind with nothing queued or sent" is
 * behind − queued − issued, floored at 0. When a queued player is not behind,
 * this undercounts, never overcounts: the red number is never a false alarm.
 */
export function countPlayersBehind(
    changes: PendingChange[],
    statusByZoneId: Map<number, ZoneDeliveryStatus>
): PlayersBehindCounts {
    let playersBehind = 0;
    let playersNotSent = 0;
    const countedZoneIds = new Set<number>();
    for (const status of zonesBehindStatuses(changes, statusByZoneId)) {
        for (const deliveryZone of status.deliveryZones) {
            if (countedZoneIds.has(deliveryZone.zoneId)) continue;
            countedZoneIds.add(deliveryZone.zoneId);
            playersBehind += deliveryZone.playersBehind;
            playersNotSent += Math.max(
                0,
                deliveryZone.playersBehind - deliveryZone.playersQueued - deliveryZone.playersIssued
            );
        }
    }
    return { playersBehind, playersNotSent };
}

export type SyncPillState =
    | { kind: 'loading' }
    | { kind: 'unavailable' }
    | ({ kind: 'counts' } & PlayersBehindCounts);

/**
 * What the pill shows: the icon alone while the first load runs, the unplug
 * icon when the change feed or the status cannot be read, otherwise the two
 * counts, zeros included.
 */
export function syncPillState(inputs: {
    feedLoaded: boolean;
    feedFailed: boolean;
    statusLoaded: boolean;
    statusFailed: boolean;
    /** True when the feed has no zones to ask about, so no status is needed. */
    noZonesToAsk: boolean;
    counts: PlayersBehindCounts;
}): SyncPillState {
    if (inputs.feedFailed || inputs.statusFailed) return { kind: 'unavailable' };
    if (!inputs.feedLoaded) return { kind: 'loading' };
    if (!inputs.statusLoaded && !inputs.noZonesToAsk) return { kind: 'loading' };
    return { kind: 'counts', ...inputs.counts };
}

/**
 * The two forms of the pill: "Sync 7 · 5", and the icon alone. The toolbar
 * picks one from its fit state (see searchToolbarStatusLayout).
 */
export type SyncPillForm = 'full' | 'icon';

/**
 * The pages that show the Sync pill: the lists and the record pages of players
 * and of custom lists. A change to a custom list is what leaves players behind,
 * and the players are who it leaves behind. Other pages leave the pill off, and
 * its two background requests with it.
 */
const SYNC_PILL_ENTITY_IDS: readonly string[] = ['LocationZonePlayer', 'LocationZoneCustomPlaylist'];

export function showsSyncPill(entityId: string): boolean {
    return SYNC_PILL_ENTITY_IDS.includes(entityId);
}

/**
 * What the pill draws in each form. Without the counts, a red dot on the icon
 * keeps the problem visible when something is not sent. Loading and unavailable
 * never show counts, but they show the word in the full form.
 */
export function syncPillPresentation(
    state: SyncPillState,
    form: SyncPillForm
): { showWord: boolean; showCounts: boolean; showProblemDot: boolean } {
    const showWord = form === 'full';
    if (state.kind !== 'counts') return { showWord, showCounts: false, showProblemDot: false };
    const showCounts = form === 'full';
    return { showWord, showCounts, showProblemDot: !showCounts && state.playersNotSent > 0 };
}

function playersPhrase(count: number): string {
    return `${count} ${count === 1 ? 'player' : 'players'}`;
}

/**
 * The pill's tooltip and accessible name. The pill shows only "7 · 5", so this
 * says what the numbers count. The first is the players of the changed zones
 * that do not have the new content yet. The second is the players among them
 * with no delivery queued or sent.
 */
export function syncPillLabel(state: SyncPillState): string {
    switch (state.kind) {
        case 'loading':
            return 'Loading sync status…';
        case 'unavailable':
            return 'Sync status unavailable. Click to retry.';
        case 'counts':
            if (state.playersBehind === 0) return 'Every player is up to date';
            return `${playersPhrase(state.playersBehind)} behind · ${playersPhrase(state.playersNotSent)} not sent`;
    }
}

/**
 * Whether the popover lists this zone: it has a change that needs a delivery
 * and its players do not all have it ("4 AM", "Sent" or "Not sent"). Zones
 * that are up to date, have no players, or changed only their hours are left
 * out. The same rule the pill counts by.
 */
export function isZoneBehind(zone: SyncZone, status: ZoneDeliveryStatus | undefined): boolean {
    return (
        status !== undefined &&
        zone.changes.some((change) => change.requires_publish) &&
        BEHIND_KINDS.has(deriveZoneSyncState(status).kind)
    );
}

/** The popover's top line: "9 zones behind", "9+" while older pages of the feed remain. */
export function syncPopoverSummary(zonesBehind: number, hasMorePages: boolean): string {
    if (zonesBehind === 1 && !hasMorePages) return '1 zone behind';
    return `${zonesBehind}${hasMorePages ? '+' : ''} zones behind`;
}

/** The popover's widest width, before the toolbar and the viewport narrow it. */
export const SYNC_POPOVER_PREFERRED_WIDTH = 560;

/** The space kept free on each side of the viewport (the old max-w-[calc(100vw-2rem)]). */
const VIEWPORT_MARGIN = 16;

/**
 * Below this, hanging from the pill's right edge leaves the popover too narrow to
 * read, and it opens rightwards from the pill's left edge instead.
 */
const SYNC_POPOVER_MIN_WIDTH = 320;

export interface SyncPopoverPlacement {
    /** Which of the pill's edges the popover lines up with: `end` is the right edge. */
    align: 'start' | 'end';
    width: number;
}

/**
 * Where the popover opens, and how wide it is.
 *
 * It normally hangs from the pill's right edge, so its left edge is
 * pillRight − width. It never reaches further left than the left edge of the
 * toolbar's first control (`selectorLeft`, now the list's search box), nor past
 * the viewport's margin, and never exceeds the preferred width. Without that
 * control on the page, only the viewport limits it.
 *
 * On a phone the pill can sit at the left of the pager's row, right under the
 * first control, and that rule left the popover about 85px wide. So when hanging from
 * the right edge would leave it under 320px, it lines up with the pill's left
 * edge instead and opens rightwards, as wide as the viewport allows.
 */
export function syncPopoverPlacement(
    pill: { left: number; right: number },
    selectorLeft: number | null,
    viewportWidth: number
): SyncPopoverPlacement {
    const widest = Math.min(SYNC_POPOVER_PREFERRED_WIDTH, viewportWidth - 2 * VIEWPORT_MARGIN);
    const leftmostEdge = Math.max(VIEWPORT_MARGIN, selectorLeft ?? VIEWPORT_MARGIN);
    const hangingWidth = Math.max(0, Math.floor(Math.min(widest, pill.right - leftmostEdge)));
    const openingRightWidth = Math.max(0, Math.floor(Math.min(widest, viewportWidth - VIEWPORT_MARGIN - pill.left)));

    if (hangingWidth < SYNC_POPOVER_MIN_WIDTH && openingRightWidth > hangingWidth) {
        return { align: 'start', width: openingRightWidth };
    }
    return { align: 'end', width: hangingWidth };
}

// ---------------------------------------------------------------------------
// Player state
// ---------------------------------------------------------------------------

const CONTENT_COMMAND_VERBS = ['ADD', 'ADDCUSTOM', 'ADDGENERATED'];

/** The command's verb, the way the backend reads it (LocationZonePlayerCommandHistory::verbOf). */
export function commandVerb(command: string): string {
    if (command.startsWith('{')) {
        const match = /^\{\s*"command"\s*:\s*"([^"\\]+)"/.exec(command);
        return match ? match[1].toUpperCase() : '';
    }
    return command.split('#', 1)[0].toUpperCase();
}

export function isContentCommand(command: string): boolean {
    return CONTENT_COMMAND_VERBS.includes(commandVerb(command));
}

/**
 * The newest content command the player was sent, from its command history
 * (newest first). Skipped commands were never sent and untracked ones were not
 * sent by tabasco, so both are passed over, as the backend's
 * latestTrackedContentCommand does.
 */
export function latestContentCommand(history: CommandHistoryEntry[]): CommandHistoryEntry | null {
    return (
        history.find(
            (entry) => entry.state !== 'skipped' && entry.source !== 'untracked' && isContentCommand(entry.command)
        ) ?? null
    );
}

export type PlayerSyncKind = 'offline' | 'waiting' | 'downloading' | 'received' | 'sent' | 'up_to_date' | 'not_sent';

export interface PlayerSyncState {
    kind: PlayerSyncKind;
    /** When the step happened: received or sent. */
    at: string | null;
    /** Tracks the player still has to download, while it is downloading. */
    tracksLeft: number | null;
}

export interface PlayerSyncInputs {
    /** Seconds since the player last checked in, or null when it never has. */
    checkinSeconds: number | null;
    /** True when a content delivery is queued for this player (it goes out at 4 AM). */
    hasQueuedContentDelivery: boolean;
    latestContentCommand: CommandHistoryEntry | null;
    /** When the zone's content last changed. Null when it has not changed since this feature shipped. */
    contentChangedAt: string | null;
    referencedContentMissing: number | null;
}

/**
 * Milliseconds for a server time. The command history sends ISO 8601 with an
 * offset. The status endpoint sends content_changed_at straight from MySQL,
 * "2026-10-23 14:14:00.123", which is UTC with no offset written. A browser
 * reads that form as local time, so it is read as UTC here.
 */
export function serverTimeMs(value: string): number {
    const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
    return new Date(hasOffset ? value : `${value.replace(' ', 'T')}Z`).getTime();
}

function isBefore(left: string | null, right: string | null): boolean {
    if (left === null || right === null) {
        return false;
    }
    return serverTimeMs(left) < serverTimeMs(right);
}

/**
 * Where one player is with the current content.
 *
 * A player that has not checked in within the online window is offline,
 * whatever else is true, because nothing reaches it until it checks in.
 * After that: a queued delivery is waiting. Otherwise the newest content
 * command decides, but only when it was issued after the content changed. A
 * command older than the change carries the old content.
 */
export function derivePlayerSyncState(inputs: PlayerSyncInputs): PlayerSyncState {
    if (inputs.checkinSeconds === null || inputs.checkinSeconds >= ONLINE_WITHIN_SECONDS) {
        return { kind: 'offline', at: null, tracksLeft: null };
    }
    if (inputs.hasQueuedContentDelivery) {
        return { kind: 'waiting', at: null, tracksLeft: null };
    }
    const command = inputs.latestContentCommand;
    if (command === null || isBefore(command.issued_at, inputs.contentChangedAt)) {
        return inputs.contentChangedAt === null
            ? { kind: 'up_to_date', at: null, tracksLeft: null }
            : { kind: 'not_sent', at: null, tracksLeft: null };
    }
    if (command.completed_at !== null) {
        return { kind: 'up_to_date', at: command.completed_at, tracksLeft: null };
    }
    if (command.delivered_at !== null) {
        const tracksLeft =
            inputs.referencedContentMissing !== null && inputs.referencedContentMissing > 0
                ? inputs.referencedContentMissing
                : null;
        return { kind: 'downloading', at: command.delivered_at, tracksLeft };
    }
    if (command.acknowledged_at !== null) {
        return { kind: 'received', at: command.acknowledged_at, tracksLeft: null };
    }
    return { kind: 'sent', at: command.issued_at, tracksLeft: null };
}

function checkinAgo(checkinSeconds: number | null): string {
    return checkinSeconds === null ? 'never checked in' : `checked in ${formatCompactDuration(checkinSeconds)} ago`;
}

/**
 * The labels a player line may put in the popover's state column. The same
 * small words the zone rows use, so expanding a zone never widens that column
 * past the zone states.
 */
export const PLAYER_SHORT_LABELS = [
    NIGHTLY_DELIVERY_LABEL,
    'Sent',
    'Received',
    'Downloading',
    'Up to date',
    'Not sent',
    'Offline',
] as const;

export type PlayerShortLabel = (typeof PLAYER_SHORT_LABELS)[number];

/** The player line's state column: one of {@link PLAYER_SHORT_LABELS}. */
export function playerSyncShortLabel(state: PlayerSyncState): PlayerShortLabel {
    switch (state.kind) {
        case 'offline':
            return 'Offline';
        case 'waiting':
            return NIGHTLY_DELIVERY_LABEL;
        case 'downloading':
            return 'Downloading';
        case 'received':
            return 'Received';
        case 'sent':
            return 'Sent';
        case 'up_to_date':
            return 'Up to date';
        case 'not_sent':
            return 'Not sent';
    }
}

/**
 * The player line's muted detail, in the zone column: "checked in 9s ago",
 * "12 tracks left · checked in 22s ago", "received 1:06 PM".
 */
export function playerSyncDetailText(state: PlayerSyncState, checkinSeconds: number | null, nowMs: number): string {
    switch (state.kind) {
        case 'offline':
            return checkinSeconds === null ? 'never checked in' : `last ${checkinAgo(checkinSeconds)}`;
        case 'downloading':
            return state.tracksLeft !== null
                ? `${state.tracksLeft} tracks left · ${checkinAgo(checkinSeconds)}`
                : checkinAgo(checkinSeconds);
        case 'received':
            return `received ${formatSyncTime(state.at, nowMs)}`.trim();
        case 'sent':
            return `sent ${formatSyncTime(state.at, nowMs)}`.trim();
        case 'waiting':
        case 'up_to_date':
        case 'not_sent':
            return checkinAgo(checkinSeconds);
    }
}

/** A queued command is a content delivery when its verb is a content verb (the queue books ADDCUSTOM). */
export function hasQueuedContentDelivery(queued: Array<{ command: string }>): boolean {
    return queued.some((entry) => isContentCommand(entry.command));
}
